import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  obtenerNotificaciones,
  conteoNotificaciones,
  marcarLeida,
  marcarTodasLeidas,
} from "../../api/notificacionesApi";

const TIPO_CONFIG = {
  capacitacion: { emoji: '🎓', color: '#3b82f6' },
  servicio: { emoji: '🛠️', color: '#f59e0b' },
  hallazgo: { emoji: '⚠️', color: '#dc2626' },
  sistema: { emoji: '💬', color: '#6b7280' },
};

export default function NotificacionesBadge() {
  const isDark = true;
  const navigate = useNavigate();
  const dropdownRef = useRef(null);

  const colors = {
    dropdownBg: isDark ? '#111111' : '#ffffff',
    dropdownBorder: isDark ? 'rgba(255, 255, 255, 0.1)' : '#dee2e6',
    text: isDark ? '#ffffff' : '#212529',
    textSecondary: isDark ? 'rgba(255, 255, 255, 0.7)' : '#6c757d',
    textMuted: isDark ? 'rgba(255, 255, 255, 0.5)' : '#9ca3af',
    itemHover: isDark ? 'rgba(255, 255, 255, 0.05)' : '#f8f9fa',
    unreadBg: isDark ? 'rgba(59, 130, 246, 0.1)' : '#eff6ff',
  };

  const [count, setCount] = useState(0);
  const [notificaciones, setNotificaciones] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);

  // Polling cada 30 segundos
  useEffect(() => {
    fetchCount();
    const interval = setInterval(fetchCount, 30000);
    return () => clearInterval(interval);
  }, []);

  // Cerrar dropdown al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchCount = useCallback(async () => {
    try {
      const data = await conteoNotificaciones();
      setCount(data.count);
    } catch (_) {}
  }, []);

  const handleToggle = async () => {
    if (!showDropdown) {
      setLoading(true);
      try {
        const data = await obtenerNotificaciones();
        setNotificaciones(data);
      } catch (error) {
        console.error('Error al obtener notificaciones:', error);
      } finally {
        setLoading(false);
      }
    }
    setShowDropdown(!showDropdown);
  };

  const handleClick = async (notif) => {
    try {
      await marcarLeida(notif.id);
      setCount(prev => Math.max(0, prev - 1));
      setNotificaciones(prev => prev.filter(n => n.id !== notif.id));
      setShowDropdown(false);
      if (notif.link) navigate(notif.link);
    } catch (_) {}
  };

  const handleMarcarTodas = async () => {
    try {
      await marcarTodasLeidas();
      setCount(0);
      setNotificaciones([]);
      setShowDropdown(false);
    } catch (_) {}
  };

  const timeAgo = (dateStr) => {
    const now = new Date();
    const date = new Date(dateStr);
    const diffMs = now - date;
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'ahora';
    if (diffMin < 60) return `hace ${diffMin}m`;
    const diffHrs = Math.floor(diffMin / 60);
    if (diffHrs < 24) return `hace ${diffHrs}h`;
    const diffDays = Math.floor(diffHrs / 24);
    return `hace ${diffDays}d`;
  };

  return (
    <div ref={dropdownRef} style={{ position: 'relative' }}>
      {/* Botón campana */}
      <button
        onClick={handleToggle}
        style={{
          background: "transparent",
          border: "none",
          color: "rgba(255, 255, 255, 0.7)",
          padding: "8px",
          borderRadius: "50%",
          cursor: "pointer",
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "all 0.2s ease",
        }}
        onMouseOver={(e) => e.currentTarget.style.color = '#ffffff'}
        onMouseOut={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.7)'}
        title="Notificaciones"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
          <path d="M8 16a2 2 0 0 0 2-2H6a2 2 0 0 0 2 2zM8 1.918l-.797.161A4.002 4.002 0 0 0 4 6c0 .628-.134 2.197-.459 3.742-.16.767-.376 1.566-.663 2.258h10.244c-.287-.692-.502-1.49-.663-2.258C12.134 8.197 12 6.628 12 6a4.002 4.002 0 0 0-3.203-3.92L8 1.917zM14.22 12c.223.447.481.801.78 1H1c.299-.199.557-.553.78-1C2.68 10.2 3 6.88 3 6c0-2.42 1.72-4.44 4.005-4.901a1 1 0 1 1 1.99 0A5.002 5.002 0 0 1 13 6c0 .88.32 4.2 1.22 6z"/>
        </svg>
        {count > 0 && (
          <span style={{
            position: "absolute",
            top: "2px",
            right: "2px",
            background: "#ef4444",
            color: "#fff",
            fontSize: "0.6rem",
            fontWeight: 800,
            minWidth: "16px",
            height: "16px",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            lineHeight: 1,
          }}>
            {count > 9 ? '9+' : count}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {showDropdown && (
        <div style={{
          position: 'absolute',
          top: '100%',
          right: 0,
          width: '320px',
          maxHeight: '400px',
          backgroundColor: colors.dropdownBg,
          border: `1px solid ${colors.dropdownBorder}`,
          borderRadius: '12px',
          boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
          zIndex: 1050,
          overflow: 'hidden',
        }}>
          {/* Header */}
          <div style={{
            padding: '12px 16px',
            borderBottom: `1px solid ${colors.dropdownBorder}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            <span style={{ fontWeight: 600, fontSize: '0.9rem', color: colors.text }}>
              Notificaciones
            </span>
            {notificaciones.length > 0 && (
              <button
                onClick={handleMarcarTodas}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#3b82f6',
                  fontSize: '0.75rem',
                  cursor: 'pointer',
                  padding: '2px 6px',
                }}
              >
                Marcar todas leídas
              </button>
            )}
          </div>

          {/* Lista */}
          <div style={{ maxHeight: '320px', overflowY: 'auto' }}>
            {loading ? (
              <div style={{ padding: '24px', textAlign: 'center', color: colors.textMuted }}>
                <div className="spinner-border spinner-border-sm" role="status" />
              </div>
            ) : notificaciones.length === 0 ? (
              <div style={{ padding: '24px', textAlign: 'center', color: colors.textMuted, fontSize: '0.85rem' }}>
                Sin notificaciones nuevas
              </div>
            ) : (
              notificaciones.map((n) => {
                const cfg = TIPO_CONFIG[n.tipo] || TIPO_CONFIG.sistema;
                return (
                  <div
                    key={n.id}
                    onClick={() => handleClick(n)}
                    style={{
                      padding: '10px 16px',
                      cursor: 'pointer',
                      borderBottom: `1px solid ${colors.dropdownBorder}`,
                      backgroundColor: colors.unreadBg,
                      transition: 'background 0.15s',
                    }}
                    onMouseOver={(e) => e.currentTarget.style.backgroundColor = colors.itemHover}
                    onMouseOut={(e) => e.currentTarget.style.backgroundColor = colors.unreadBg}
                  >
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                      <span style={{ fontSize: '1.2rem', flexShrink: 0 }}>{cfg.emoji}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: '0.8rem', color: colors.text }}>
                          {n.titulo}
                        </div>
                        {n.mensaje && (
                          <div style={{ fontSize: '0.75rem', color: colors.textSecondary, marginTop: '2px' }}>
                            {n.mensaje.length > 80 ? n.mensaje.substring(0, 80) + '...' : n.mensaje}
                          </div>
                        )}
                        <div style={{ fontSize: '0.65rem', color: colors.textMuted, marginTop: '4px' }}>
                          {timeAgo(n.created_at)}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
